import { CHARACTER_CONTACT } from './character-animator.js';
import { goblinRatMorph } from '../../../content/characters/rigs/goblin-rat.js';
import { track } from './character-motion.js';
import * as THREE from 'three';
import type { Point, UnitDefinition, UnitState } from '../../../shared/game/world-types.js';
import { createCharacterModel } from './character-model.js';
import { createGoblinRatModel, RAT_SPINE_PIVOT } from './goblin-rat-model.js';
import { foldIntoSkin } from './skin-folding.js';
import type { HeadDetail } from './human-head.js';
import { worldFigureOptions } from './create-unit-animator.js';
import { mergeRigidParts } from './rigid-batches.js';
import { createCharacterAnimator } from './create-character-animator.js';
import type { UnitAnimator } from './unit-animator.js';
import { mountedDeathRig } from './death-rig.js';

/** Metres of travel per walking stride; the gallop's stride comes from `goblinRatMorph.stride` (radians per metre). */
const WALK_STRIDE = .7;
/** Speeds (m/s) between which the walk gives way to the gallop: the patrol stroll walks, Speed 10 gallops. */
const GALLOP_FROM = 2.2, GALLOP_AT = 3.8;
/**
 * Footfalls as [touchdown, liftoff] fractions of a stride, by leg. The walk is a lateral sequence; the gallop is a rat's
 * half-bound: the hind paws land almost together and drive, a short flight, then the forepaws land one after the other.
 */
const WALK = { hind: { [-1]: [0, .66], [1]: [.5, 1.16] }, fore: { [-1]: [.25, .91], [1]: [.75, 1.41] } } as const;
const GALLOP = { hind: { [-1]: [0, .3], [1]: [.04, .34] }, fore: { [-1]: [.5, .72], [1]: [.57, .79] } } as const;
/** Gallop body motion over one stride: saddle height (m), pitch (rad, + is nose down) and spine flex (+ arches the back as the legs gather). */
const GALLOP_BOB = [[0, -.01], [.16, -.035], [.34, .02], [.42, .045], [.5, 0], [.64, -.04], [.8, .02], [.9, .055], [1, -.01]] as const;
const GALLOP_PITCH = [[0, .03], [.18, -.06], [.36, -.04], [.5, .02], [.66, .08], [.82, .03], [1, .03]] as const;
const GALLOP_FLEX = [[0, .55], [.18, .1], [.38, -.6], [.5, -.35], [.68, .25], [.9, .85], [1, .55]] as const;
/** Radians each spine bone turns at full flex. */
const SPINE_FLEX = .1;
/**
 * The rider rides the saddle on legs and seat like a spring: natural frequency (Hz) at a walk (sitting tight) and at a gallop
 * (well below the stride's beat, so the rider bobs on their own rhythm), damping ratio, and how far they may sink or rise (m).
 */
const RIDER_SPRING = [7, 2.6] as const, RIDER_DAMPING = .32, RIDER_SINK = .015, RIDER_RISE = .07;

const fraction = (value: number) => value - Math.floor(value);

/** A seated rider and an articulated rat; travel always comes from authoritative positions. */
export function createRatriderAnimator(definition: UnitDefinition, detail: HeadDetail = 'high') {
  const rider = createCharacterModel(definition, worldFigureOptions(detail)), body = createCharacterAnimator(rider, definition);
  body.seated = true;
  const mount = createGoblinRatModel(definition.appearance!.cloth), root = new THREE.Group();
  root.add(mount.root, rider.root);
  rider.root.rotation.order = 'YXZ';
  const scale = rider.root.scale.y;
  const seatHeight = 1.015 * goblinRatMorph.height + goblinRatMorph.riderElevation - .2;
  const seat = new THREE.Vector3(0, seatHeight / scale, -.12 * goblinRatMorph.length / scale);
  const legLength = (-rider.leftLeg.lower.position.y - rider.leftLeg.hand.position.y) * scale;
  const footWidth = .43 * goblinRatMorph.width;
  const dx = footWidth - Math.abs(rider.leftLeg.upper.position.x) * scale;
  const drop = Math.sqrt(Math.max(.01, (legLength * .9) ** 2 - dx ** 2));
  const foot = new THREE.Vector3(footWidth / scale, (seatHeight + .07 * scale - drop) / scale, -.1 * goblinRatMorph.length / scale);
  for (const { ring, strap } of mount.stirrups) {
    const y = foot.y * scale / goblinRatMorph.height;
    // The leathers hang from under the seat's edge, not from above it.
    ring.position.y = y - .02;
    strap.position.y = (y + .86) / 2; strap.scale.y = Math.max(.02, .86 - y) / .24;
  }
  // The rat's anatomy and tack never change shape after this: one skinned draw per material instead of ~35 pieces. The ears stay
  // rigid: their skin and hollow lie millimetres apart, and skinning both into one draw let the hollow flicker through the rim.
  const ears: THREE.Object3D[] = []; mount.root.traverse(object => { if (object.name === 'rat-ear') ears.push(object); });
  foldIntoSkin(mount.root, ears);
  mergeRigidParts(mount.root);
  const headRest = mount.head.position.clone();
  let previous: Point | undefined, strides = 0, speed = 0, gallop = 0, travelWeight = 0, lastTime = -1, deathAt: number | undefined;
  let riderY = 0, riderVelocity = 0, saddleY = 0;
  const height = Math.max(1.5, seatHeight + (rider.height - rider.proportions.hipsY * scale));
  const grip = new THREE.Vector3(), lifted = new THREE.Vector3(), mark = new THREE.Vector3(), frame = new THREE.Matrix4(), inverse = new THREE.Matrix4(), tilt = new THREE.Quaternion(), euler = new THREE.Euler(0, 0, 0, 'YXZ');
  const morphScale = new THREE.Vector3(goblinRatMorph.width, goblinRatMorph.height, goblinRatMorph.length);
  const animator: UnitAnimator = {
    rig: { height }, onStep: null,
    get settledCorpse() { return body.settledCorpse && deathAt !== undefined && lastTime - deathAt >= 2; },
    get animateSettledCorpses() { return body.animateSettledCorpses; },
    set animateSettledCorpses(value: boolean) { body.animateSettledCorpses = value; },
    reset() {
      body.reset(); previous = undefined; riderY = riderVelocity = saddleY = 0; strides = 0; speed = 0; gallop = 0; travelWeight = 0; lastTime = -1; deathAt = undefined;
      mount.root.position.y = 0; mount.root.rotation.x = mount.root.rotation.z = 0; mount.loin.rotation.x = mount.chest.rotation.x = 0;
      mount.head.position.copy(headRest); mount.head.rotation.x = 0; mount.jaw.rotation.x = 0;
    },
    stopActions: () => body.stopActions(), playing: () => body.playing(),
    sever: () => body.sever(), swingLead: at => body.swingLead(at),
    play: (...args) => body.play(...args),
    update(state: UnitState, position: Point, time: number, target?: Point, inCombat?: boolean) {
      if (time < lastTime) animator.reset();
      if (time === lastTime) return;
      if (state.health.dead && animator.settledCorpse && !body.animateSettledCorpses && previous?.x === position.x && previous.z === position.z) {
        body.update(state, position, time, target, inCombat); lastTime = time; return;
      }
      body.update(state, position, time, target, inCombat);
      const dt = previous && time > lastTime && lastTime >= 0 ? time - lastTime : 0;
      const traveled = dt ? Math.hypot(position.x - previous!.x, position.z - previous!.z) : 0;
      if (dt) speed = THREE.MathUtils.lerp(speed, traveled / dt, 1 - Math.exp(-dt * 6));
      gallop = THREE.MathUtils.lerp(gallop, THREE.MathUtils.smoothstep(speed, GALLOP_FROM, GALLOP_AT), 1 - Math.exp(-dt * 5));
      // Paws are planted in metres of ground: each stride covers exactly `stride` metres of authoritative travel.
      const stride = THREE.MathUtils.lerp(WALK_STRIDE, Math.PI * 2 / goblinRatMorph.stride, gallop) * goblinRatMorph.length;
      strides += traveled / stride;
      const moving = traveled > .0001, u = fraction(strides);
      travelWeight = THREE.MathUtils.lerp(travelWeight, moving ? 1 : 0, 1 - Math.exp(-dt * 12));
      mount.root.rotation.y = rider.root.rotation.y;
      // The rat bites with the rider's thrust, whether the rider is following its windup or playing a quick strike.
      const thrust = body.playing(), bite = thrust && (thrust.kind === 'slash' || thrust.kind === 'thrust') ? thrust.progress : undefined;
      mount.jaw.rotation.x = bite !== undefined ? track(bite, [[0, 0], [.23, .18], [.42, .7], [CHARACTER_CONTACT.thrust, 0], [.75, 0], [1, 0]]) : 0;
      if (!state.health.dead) {
        const p = bite ?? 0;
        const load = bite === undefined ? 0 : track(p, [[0, 0], [.28, 1], [.43, .95], [.52, .15], [.63, .4], [.8, .12], [1, 0]]);
        const drive = bite === undefined ? 0 : track(p, [[0, 0], [.42, 0], [.52, 1], [.61, 1.08], [.76, .75], [1, 0]]);
        const pitch = bite === undefined ? 0 : track(p, [[0, 0], [.28, -.035], [.43, -.025], [.52, .07], [.63, .1], [.78, .07], [1, 0]]);
        const turn = Math.PI * 2 * u, idle = 1 - travelWeight;
        const travelBob = travelWeight * THREE.MathUtils.lerp(.012 * Math.cos(turn * 2), track(u, GALLOP_BOB), gallop) + Math.sin(time * 1.7) * .003 * idle;
        const travelPitch = travelWeight * THREE.MathUtils.lerp(.012 * Math.sin(turn * 2), track(u, GALLOP_PITCH), gallop);
        const flex = travelWeight * gallop * track(u, GALLOP_FLEX);
        const bob = travelBob - load * .05 - drive * .025;
        mount.root.position.y = bob;
        mount.root.rotation.x = travelPitch + pitch;
        mount.root.rotation.z = Math.sin(turn) * .02 * travelWeight * (1 - gallop);
        mount.loin.rotation.x = -flex * SPINE_FLEX; mount.chest.rotation.x = flex * SPINE_FLEX;
        // Both roots share world units: the rider follows the saddle's compression and pitch.
        // The rider lags the saddle and bobs on their own beat: thrown up as it rises, settling back as it falls.
        const saddleVelocity = dt ? (bob - saddleY) / dt : 0, steps = Math.min(60, Math.ceil(dt * 240));
        for (let i = 0; i < steps; i++) {
          const h = dt / steps, omega = Math.PI * 2 * THREE.MathUtils.lerp(RIDER_SPRING[0], RIDER_SPRING[1], gallop);
          riderVelocity += (omega * omega * (bob - riderY) + 2 * RIDER_DAMPING * omega * (saddleVelocity - riderVelocity)) * h;
          riderY += riderVelocity * h;
        }
        if (!dt || dt > .25) { riderY = bob; riderVelocity = 0; }
        riderY = THREE.MathUtils.clamp(riderY, bob - RIDER_SINK, bob + RIDER_RISE); saddleY = bob;
        rider.root.position.y = bob;
        rider.root.rotation.x = mount.root.rotation.x;
        rider.root.rotation.z = mount.root.rotation.z;
        mount.root.updateMatrix(); mount.loin.updateMatrix(); mount.chest.updateMatrix();
        // The free fist holds the reins on the grab handle, which rides the chest.
        grip.copy(mount.grip).applyMatrix4(mount.chest.matrix.clone().multiply(new THREE.Matrix4().makeTranslation(-RAT_SPINE_PIVOT.x, -RAT_SPINE_PIVOT.y, -RAT_SPINE_PIVOT.z))).multiply(morphScale).divideScalar(scale);
        // Only the seat lifts: feet stay in the stirrups and the fist on the handle.
        const lift = (riderY - bob) / scale;
        body.poseMounted(seat, lifted.copy(foot).setY(foot.y - lift), grip.setY(grip.y - lift), lift, travelWeight * gallop);
        // Paw marks live in the ground frame (the rat's heading, without its bob, pitch or roll): planted paws stay put.
        frame.compose(new THREE.Vector3(0, bob, 0), tilt.setFromEuler(euler.set(mount.root.rotation.x, 0, mount.root.rotation.z)), morphScale);
        const toBody = inverse.copy(frame).invert();
        for (const leg of mount.legs) {
          const spine = leg.rear ? mount.loin : mount.chest;
          const walk = (leg.rear ? WALK.hind : WALK.fore)[leg.side as -1 | 1], run = (leg.rear ? GALLOP.hind : GALLOP.fore)[leg.side as -1 | 1];
          const down = THREE.MathUtils.lerp(walk[0], run[0], gallop), duty = THREE.MathUtils.lerp(walk[1] - walk[0], run[1] - run[0], gallop);
          const sweep = duty * stride, toe = (leg.rear ? .12 : .08) * goblinRatMorph.length;
          // Where the toes rest under a standing rat; at a gallop the hind paws reach a little further forward.
          const rest = (leg.rear ? -.565 + .12 + gallop * .04 : .35 + .08) * goblinRatMorph.length;
          const step = fraction(u - down);
          let along: number, lift = 0, roll: number;
          if (step < duty) {
            // Stance: the toes stay on their mark while the body passes over them; the heel peels up before lift-off.
            const q = step / duty; along = sweep / 2 - q * sweep;
            roll = (leg.rear ? .55 : .35) * THREE.MathUtils.smoothstep(q, .5, 1);
          } else {
            // Swing: the paw curls, clears the ground and reaches forward to its next mark.
            // It leaves and meets the ground still drifting back with it, so neither lift-off nor touchdown scuffs.
            const q = (step - duty) / (1 - duty), arc = Math.sin(Math.PI * q), drift = -sweep * (1 - duty) / duty * .5;
            along = -sweep / 2 + sweep * q * q * (3 - 2 * q) + drift * (q * (1 - q) ** 2 - q * q * (1 - q));
            lift = arc * THREE.MathUtils.lerp(leg.rear ? .07 : .06, leg.rear ? .15 : .12, gallop) * goblinRatMorph.height;
            roll = (leg.rear ? .55 : .35) * (1 - q) + arc * .5;
          }
          along *= travelWeight; lift *= travelWeight; roll *= travelWeight;
          const ankle = (leg.rear ? .04 : .024) * goblinRatMorph.height;
          mark.set(0, ankle + lift + toe * Math.sin(roll), rest + along - toe * Math.cos(roll)).applyMatrix4(toBody);
          mark.sub(spine.position).applyQuaternion(spine.quaternion.clone().invert());
          // Solve toward the ground mark through body compression, pitch and spine flex, rather than carrying a planted paw with the torso.
          const hipY = leg.upper.position.y, hipZ = leg.upper.position.z;
          const rawY = mark.y - hipY, rawZ = mark.z - hipZ, rawDistance = Math.max(.001, Math.hypot(rawY, rawZ));
          const distance = THREE.MathUtils.clamp(rawDistance, Math.abs(leg.upperLength - leg.lowerLength) + .001, leg.upperLength + leg.lowerLength - .001);
          const dy = rawY / rawDistance * distance, dz = rawZ / rawDistance * distance;
          const reach = (leg.upperLength ** 2 - leg.lowerLength ** 2 + distance ** 2) / (2 * distance);
          const bend = Math.sqrt(Math.max(0, leg.upperLength ** 2 - reach ** 2));
          // The foreleg elbow folds back. The larger hind knee points forward above the hock.
          const sign = leg.rear ? -1 : 1;
          const kneeY = hipY + dy / distance * reach + sign * (-dz / distance) * bend;
          const kneeZ = hipZ + dz / distance * reach + sign * (dy / distance) * bend;
          const thighY = kneeY - hipY, thighZ = kneeZ - hipZ;
          const shinY = hipY + dy - kneeY, shinZ = hipZ + dz - kneeZ;
          const thighAngle = Math.atan2(-thighZ, -thighY), shinAngle = Math.atan2(-shinZ, -shinY);
          leg.upper.rotation.x = thighAngle;
          leg.lower.rotation.x = shinAngle - thighAngle;
          leg.paw.rotation.x = -shinAngle - mount.root.rotation.x - spine.rotation.x + roll;
        }
        // The tail streams out behind a gallop and whips with the spine.
        mount.tail.rotation.y = Math.sin(time * 2 + strides * 1.3) * .09 * (1 - gallop * .6) + (bite === undefined ? 0 : track(p, [[0, 0], [.42, -.08], [.68, .13], [.85, .04], [1, 0]]));
        mount.tail.rotation.x = travelWeight * gallop * (.14 - flex * .12);
        mount.head.position.copy(headRest); mount.head.position.z += drive * .14;
        // At a gallop the head holds its line against the body's pitching.
        mount.head.rotation.x = (moving ? Math.sin(turn * 2) * .035 * (1 - gallop) - (travelPitch + flex * SPINE_FLEX) * .7 * gallop : Math.sin(time * 1.7) * .02) + drive * .08 - load * .04;
        deathAt = undefined;
      } else {
        deathAt ??= time;
        const collapse = THREE.MathUtils.smoothstep(time - deathAt, 0, 2);
        mount.root.rotation.z = 1.2 * collapse; mount.root.position.y = -.15 * collapse;
        mount.loin.rotation.x = mount.chest.rotation.x = 0;
        rider.root.position.y = -.15 * collapse / scale;
        for (const leg of mount.legs) { leg.upper.rotation.x = -.6 * collapse; leg.lower.rotation.x = .8 * collapse; }
      }
      previous = { ...position }; lastTime = time;
    },
    dispose() { body.dispose(); mount.dispose(); rider.root.traverse(object => { if (object instanceof THREE.Mesh) object.geometry.dispose(); }); },
  };
  body.onStep = () => animator.onStep?.();
  return { animator, rig: { root, height }, deathRig: mountedDeathRig(root, rider, mount) };
}
